/**
 * Layers panel — select, hide, lock, rename, duplicate, reorder.
 */

import { useEffect, useState } from 'react';
import type { fabric } from 'fabric';
import {
  deleteObject,
  duplicateObject,
  isObjectLocked,
  renameObject,
  setObjectLocked,
} from '@/lib/layerOps';
import { isCropOverlay } from '@/lib/cropTool';
import { isGuideObject } from '@/lib/guideLines';
import { isLassoOverlay } from '@/lib/lassoTool';
import { isMarqueeOverlay } from '@/lib/marqueeTool';
import { ensureObjectUid } from '@/lib/objectUid';
import { readLayerAnimation } from '@/lib/video/layerAnimation';

function isLayerObject(obj: fabric.Object): boolean {
  if (isGuideObject(obj) || isMarqueeOverlay(obj) || isLassoOverlay(obj)) return false;
  if (isCropOverlay(obj) || obj.excludeFromExport) return false;
  return true;
}

interface LayersPanelProps {
  canvas: fabric.Canvas | null;
  ready: boolean;
  onChange?: () => void;
}

function layerLabel(obj: fabric.Object, index: number): string {
  const typed = obj as fabric.Object & {
    text?: string;
    elementId?: string;
    name?: string;
  };

  if (typed.name) return typed.name;
  if (typed.elementId) return typed.elementId.replace(/^(shape|text|frame)-/, '');
  if (typeof typed.text === 'string' && typed.text.trim()) {
    const t = typed.text.trim().replace(/\s+/g, ' ');
    return t.length > 22 ? `${t.slice(0, 22)}…` : t;
  }

  const type = (obj.type || 'object').toString();
  return `${type} ${index + 1}`;
}

export default function LayersPanel({ canvas, ready, onChange }: LayersPanelProps) {
  const [layers, setLayers] = useState<fabric.Object[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (!canvas || !ready) return;

    const sync = () => {
      const objects = canvas.getObjects().filter(isLayerObject).slice().reverse();
      setLayers(objects);
      const active = canvas.getActiveObject();
      setActiveId(
        active
          ? String(
              (active as fabric.Object & { __uid?: string }).__uid ??
                objects.indexOf(active)
            )
          : null
      );
    };

    canvas.getObjects().forEach((obj) => {
      ensureObjectUid(obj);
      if (isObjectLocked(obj)) setObjectLocked(obj, true);
    });

    sync();

    const events = [
      'object:added',
      'object:removed',
      'object:modified',
      'selection:created',
      'selection:updated',
      'selection:cleared',
    ] as const;

    events.forEach((ev) => canvas.on(ev, sync));
    return () => {
      events.forEach((ev) => canvas.off(ev, sync));
    };
  }, [canvas, ready]);

  const notify = () => {
    canvas?.fire('object:modified', { target: canvas.getActiveObject() || undefined });
    onChange?.();
  };

  const selectLayer = (obj: fabric.Object) => {
    if (!canvas) return;
    canvas.discardActiveObject();
    canvas.setActiveObject(obj);
    canvas.requestRenderAll();
    const o = obj as fabric.Object & { __uid?: string };
    setActiveId(o.__uid ?? null);
  };

  const moveLayer = (obj: fabric.Object, direction: 'up' | 'down') => {
    if (!canvas) return;
    if (direction === 'up') canvas.bringForward(obj);
    else canvas.sendBackwards(obj);
    canvas.requestRenderAll();
    notify();
  };

  const toggleVisibility = (obj: fabric.Object) => {
    if (!canvas) return;
    obj.visible = !obj.visible;
    if (!obj.visible && canvas.getActiveObject() === obj) {
      canvas.discardActiveObject();
    }
    canvas.requestRenderAll();
    notify();
  };

  const toggleLock = (obj: fabric.Object) => {
    if (!canvas) return;
    const next = !isObjectLocked(obj);
    setObjectLocked(obj, next);
    if (next && canvas.getActiveObject() === obj) {
      // keep selection highlight via active object even if not evented
      canvas.setActiveObject(obj);
    }
    canvas.requestRenderAll();
    notify();
  };

  const renameLayer = (obj: fabric.Object) => {
    const current = (obj as fabric.Object & { name?: string }).name || layerLabel(obj, 0);
    const next = window.prompt('Layer name', current);
    if (next == null) return;
    renameObject(obj, next);
    setLayers((prev) => prev.slice());
    notify();
  };

  const dupLayer = (obj: fabric.Object) => {
    if (!canvas) return;
    void duplicateObject(canvas, obj).then((cloned) => {
      if (cloned) notify();
    });
  };

  const deleteLayer = (obj: fabric.Object) => {
    if (!canvas) return;
    if (isObjectLocked(obj)) return;
    if (!deleteObject(canvas, obj)) return;
    notify();
  };

  return (
    <div className="layers-panel">
      <h3>Layers</h3>
      <div className="layers-list">
        {layers.length === 0 && (
          <p className="layers-empty">No objects yet. Add elements or draw on the canvas.</p>
        )}
        {layers.map((obj, index) => {
          const o = obj as fabric.Object & { __uid?: string };
          const id = o.__uid || String(index);
          const isActive = canvas?.getActiveObject() === obj || activeId === id;
          const locked = isObjectLocked(obj);

          return (
            <div
              key={id}
              className={`layer-item ${isActive ? 'active' : ''} ${
                obj.visible === false ? 'hidden' : ''
              } ${locked ? 'locked' : ''}`}
            >
              <button
                type="button"
                className="layer-select"
                onClick={() => selectLayer(obj)}
                onDoubleClick={() => renameLayer(obj)}
                title="Select · double-click to rename"
              >
                <span className="layer-type">{(obj.type || 'obj').toString()}</span>
                <span className="layer-name">{layerLabel(obj, layers.length - 1 - index)}</span>
                {readLayerAnimation(obj) && (
                  <span className="layer-motion-badge" title="Animated">
                    ◆
                  </span>
                )}
              </button>
              <div className="layer-actions">
                <button
                  type="button"
                  title={obj.visible === false ? 'Show' : 'Hide'}
                  onClick={() => toggleVisibility(obj)}
                >
                  {obj.visible === false ? '○' : '●'}
                </button>
                <button
                  type="button"
                  title={locked ? 'Unlock' : 'Lock'}
                  onClick={() => toggleLock(obj)}
                >
                  {locked ? '■' : '□'}
                </button>
                <button type="button" title="Duplicate" onClick={() => dupLayer(obj)}>
                  +
                </button>
                <button type="button" title="Bring forward" onClick={() => moveLayer(obj, 'up')}>
                  ↑
                </button>
                <button type="button" title="Send backward" onClick={() => moveLayer(obj, 'down')}>
                  ↓
                </button>
                <button
                  type="button"
                  className="layer-delete"
                  title={locked ? 'Unlock to delete' : 'Delete'}
                  disabled={locked}
                  onClick={() => deleteLayer(obj)}
                >
                  ×
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
